import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { download } from 'ducks/reviewDocuments';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';
import { faSearchMinus } from '@fortawesome/pro-solid-svg-icons/faSearchMinus';
import { faSearchPlus } from '@fortawesome/pro-solid-svg-icons/faSearchPlus';

/**
 * Renders viewer controls.
 *
 * This component maintains orientation state and passes it as the props `zoom` and `rotation` to child components.
 *
 * @param children child components
 * @param document the document being displayed
 * @return {JSX.Element}
 */
const ViewerControls = ({ children, document }) => {
  const [zoom, setZoom] = useState(1.25);
  const [rotation, setRotation] = useState(0);
  const sentinelRef = useRef();

  // toggles "fixed" classname when element goes offscreen, used to affix controls at the top of the browser
  const observer = useMemo(() => {
    return new IntersectionObserver(
      ([e]) => e.target.classList.toggle('fixed', e.intersectionRatio === 0.0),
      { threshold: 0.0, rootMargin: '-12px' }
    );
  }, []);

  useEffect(() => {
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
      return () => observer.unobserve(sentinelRef.current);
    }
  }, [observer, sentinelRef.current]);

  return (<React.Fragment>
    <div ref={sentinelRef} className="review-viewer-controls-sentinel"/>
    <div className="review-viewer-controls">
      <button className="btn btn-primary" title="Zoom Out" onClick={() => setZoom(zoom - .25)}>
        <FontAwesomeIcon icon={faSearchMinus}/>
      </button>
      <button className="btn btn-primary" title="Zoom In" onClick={() => setZoom(zoom + .25)}>
        <FontAwesomeIcon icon={faSearchPlus}/>
      </button>
      <button className="btn btn-primary" title="Rotate" onClick={() => setRotation(rotation + 90 % 360)}>
        <FontAwesomeIcon icon={faRedo}/>
      </button>
      <button className="btn btn-primary" title="Download" onClick={() => download(document)}>
        <FontAwesomeIcon icon={faDownload}/>
      </button>
    </div>
    {React.Children.map(children, child => React.cloneElement(child, { zoom, rotation }))}
  </React.Fragment>);
};

ViewerControls.propTypes = {
  children: PropTypes.object,
  document: PropTypes.object,
};

export default ViewerControls;
